import { ConfigProvider } from 'antd'
import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { TreeSelect, type TreeSelectOption } from '../../app-core/lib/ant-overrides/AntTreeSelect'
import { getAntConfig } from '../../app-core/lib/ant-overrides/ant-config'
import { uniq } from '../lib/functional/array.no-deps'
import { isNotNil } from '../lib/functional/nil.no-deps'
import { booleanAttributeSchema } from './helpers/yup'

export enum Events {
    onChange = 'on-change',
}

interface CustomEventPayload {
    [Events.onChange]: number[]
}

export interface CustomEvents {
    [Events.onChange]: CustomEvent<CustomEventPayload['on-change']>
}

/**
 * used for subject unit select
 */
@customElement('ant-tree-unit-select')
export class UnitTreeSelectWrapper extends LitElement {
    @property({ type: Object, attribute: 'data-props' })
    props: Omit<React.ComponentProps<typeof TreeSelect>, 'onChange' | 'value' | 'treeData'>

    @property({
        type: Boolean,
        attribute: 'are-parents-selectable',
        converter: (val) => booleanAttributeSchema.validateSync(val),
    })
    areParentsSelectable: boolean = true

    @property({
        type: Object,
        attribute: 'data-tree',
        hasChanged(value: any[] = [], oldValue: any[] = []) {
            const oldIds = new Set(value.map(({ id }) => id))
            return oldValue.length !== value.length || value.some(({ id }) => !oldIds.has(id))
        },
    })
    treeData: React.ComponentProps<typeof TreeSelect>['treeData']

    @property({ type: Array, attribute: 'data-value' })
    value: React.ComponentProps<typeof TreeSelect>['value']

    @property({
        type: Number,
        attribute: 'subject-unit-type-id',
    })
    subjectUnitType: number

    /** dot separated path e.g. `data.image` within each item of `this.props.treeData` */
    @property({ attribute: 'image-path' })
    imagePath: string

    // Cached version of processed tree data. We used to compute this in render,
    // this that caused React to re-render the component every time. Now we
    // just compute when this is changed.
    _computedTreeData: TreeSelectOption[] = []

    updated(changedProps: Map<string, unknown>) {
        if (changedProps.has('treeData') || changedProps.has('subjectUnitType')) {
            this._computedTreeData = this.computeTreeData()
            this.requestUpdate()
        }
    }

    private withChildren(parent: TreeSelectOption): TreeSelectOption {
        const nodeWithImage = this.resolveImagePaths(parent)
        const matchesSubjectType = this.subjectUnitType === parent.type

        if (!matchesSubjectType && !this.doesNodeHaveDescendantsOfUnitType(parent, this.subjectUnitType)) {
            return null
        }

        const children = this.treeData
            .filter((unit) => parent.children?.includes(unit.id))
            .map(this.withChildren.bind(this))
            .filter(isNotNil)

        return {
            ...nodeWithImage,
            id: parent.id.toString(),
            selectable: this.areParentsSelectable || children.length === 0,
            children,
        }
    }

    private doesNodeHaveDescendantsOfUnitType(node: TreeSelectOption, unitTypeId: number): boolean {
        return (
            node.children?.some((childId) => {
                const child = this.treeData.find((unit) => unit.id === childId)
                return isNotNil(child)
                    ? child.type === unitTypeId || this.doesNodeHaveDescendantsOfUnitType(child, unitTypeId)
                    : false
            }) ?? false
        )
    }

    private onSelectionChanged(newValues: string[]) {
        this.dispatchEvent(
            new CustomEvent<CustomEventPayload['on-change']>(Events.onChange, {
                detail: newValues.map((a) => Number.parseInt(a, 10)),
            })
        )
    }

    private computeTreeData() {
        // eslint-disable-next-line eqeqeq
        const leafNodes = this.treeData.filter((node) => node.type == this.subjectUnitType)
        const rootParents = uniq(leafNodes.flatMap((leafNode) => this.findAllRootParentsFor(leafNode.id)))
        return rootParents.map((p) => this.withChildren(p)).filter(isNotNil)
    }

    render() {
        const innerProps: React.ComponentProps<typeof TreeSelect> = {
            fieldNames: { label: 'name', value: 'id' },
            ...this.props,
            treeDataSimpleMode: false,
            value: this.value.map((a: number) => a.toString()),
            treeData: this._computedTreeData,
            multiParents: true,
            onChange: (newValue: string[]) => {
                this.onSelectionChanged(newValue)
            },
        }

        return ReactDOM.render(
            <ConfigProvider {...getAntConfig()}>
                <TreeSelect {...innerProps} />
            </ConfigProvider>,
            this
        )
    }

    protected createRenderRoot() {
        return this
    }

    private findAllRootParentsFor(unitId: number): TreeSelectOption[] {
        const unit = this.treeData.find((item) => (item.id as number) === unitId)
        return unit.parents.length
            ? unit.parents.flatMap((parentId: number) => this.findAllRootParentsFor(parentId))
            : [unit]
    }

    private resolveImagePaths(item: TreeSelectOption) {
        const imageSource = this.imagePath ? resolveDotSeparatedObjectAccessor(this.imagePath, item) : null
        return imageSource
            ? {
                  ...item,
                  icon: <img src={imageSource}></img>,
              }
            : item
    }
}

function resolveDotSeparatedObjectAccessor(path: string, obj: Record<any, any>) {
    return path.split('.').reduce((objectValue, currentPath) => {
        return objectValue?.[currentPath]
    }, obj)
}
